import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import {
  faArrowUpRightFromSquare,
  faCheck,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { H6 } from "@verse_core/shared_components/base/TextComponents/H6";
import { HeadingXL } from "./typography";
import { Loading } from "@verse_core/shared_components/base/Loading";
import { P2 } from "@verse_core/shared_components/base/TextComponents/P2";
import { StandaloneIssueResponseType } from "./__generated__/graphql";
import { StandardButton } from "@verse_core/shared_components/buttons/StandardButton";
import { cn } from "@verse_core/shared_components/ClassName";
import { gql } from "./__generated__";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useMutation } from "@apollo/client";

const STAND_ALONE_ISSUE_RESPONSE_MUTATION = gql(`
    mutation STAND_ALONE_ISSUE_RESPONSE(
        $token: String!,
        $orderNumber: String!,
        $fromEmail: Boolean!
        $approved: Boolean!,
        $providerNoteId: Int!
        $captchaToken: String!
        $selections: [NoteResponseSelectionInput!]
    ) {
        standAloneIssueResponse(
            token: $token,
            orderNumber: $orderNumber,
            fromEmail: $fromEmail,
            approved: $approved,
            providerNoteId: $providerNoteId,
            captchaToken: $captchaToken,
            selections: $selections
        ) {
            response
            message
        }
    }
`);

const TextContent = ({
  accentTitle,
  title,
  description,
  children,
  hasImage,
  orderNumber,
  showOrderButton,
  isRetry = false,
}: {
  accentTitle?: string;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  hasImage?: boolean;
  orderNumber: string;
  showOrderButton?: boolean;
  isRetry?: boolean;
}) => {
  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-y-[24px]">
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-y-[24px]">
        {accentTitle && (
          <H6 className="tw-my-0 tw-w-full tw-text-left tw-text-blue-1">
            {accentTitle}
          </H6>
        )}
        {hasImage && (
          <img
            src="/homepage/img3.png"
            alt="Verse Medical"
            className="tw-aspect-square tw-w-[180px]"
          />
        )}
        <div
          className={cn(
            "tw-flex tw-flex-col tw-items-center tw-gap-y-[8px]",
            isRetry && "tw-gap-y-[16px]",
          )}
        >
          <HeadingXL className="tw-my-0 tw-text-blue-dark-1">{title}</HeadingXL>
          <P2 className="tw-my-0 tw-text-gray-3">{description}</P2>
        </div>
      </div>
      {children}
      {showOrderButton && (
        <StandardButton
          style="primary-blue"
          onClick={() => {
            window.open(
              `https://versemedical.com/provider_orders/view_order/${orderNumber}`,
              "_blank",
            );
          }}
        >
          View the order
        </StandardButton>
      )}
    </div>
  );
};

const StandAloneIssueResponseContent = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const orderNumber = params.get("order_number") || "";
  const token = params.get("token") || "";
  const providerNoteId = parseInt(params.get("note_id") || "");
  const approved = (params.get("approved") || "") === "true";
  const fromEmail = Boolean(params.get("from_email") || "");
  // Present when the email offered a button per response option, so the answer
  // records WHICH option was pressed rather than a bare approve.
  const specId = parseInt(params.get("spec_id") || "");
  const replacementId = parseInt(params.get("replacement_id") || "");
  const selections =
    Number.isInteger(specId) && Number.isInteger(replacementId)
      ? [
          {
            responseOptionSpecId: specId,
            messageTemplateKeyReplacementId: replacementId,
          },
        ]
      : undefined;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const handleRecaptchaVerify = async () => {
    if (!executeRecaptcha) {
      return;
    }
    const captchaToken = await executeRecaptcha("stand_alone_issue_response");
    return captchaToken;
  };

  const handleStandAloneIssueResponse = async (isApproved: boolean) => {
    const captchaToken = await handleRecaptchaVerify();

    standAloneIssueResponse({
      variables: {
        token: token,
        providerNoteId: providerNoteId,
        approved: isApproved,
        fromEmail: fromEmail,
        orderNumber: orderNumber,
        captchaToken: captchaToken || "",
        // Only when the answer matches the link that was opened. The confirm
        // and retry screens offer their own Approve/Reject, and a stored
        // selection outranks `approved` server-side -- so forwarding it on a
        // Reject would run the selection automation anyway.
        selections: isApproved === approved ? selections : undefined,
      },
    });
  };

  const [standAloneIssueResponse, { loading, data, error }] = useMutation(
    STAND_ALONE_ISSUE_RESPONSE_MUTATION,
    {
      fetchPolicy: "no-cache",
    },
  );

  const response = data?.standAloneIssueResponse.response;
  const message = error
    ? "Cannot respond to issue because of system error."
    : data?.standAloneIssueResponse.message;

  useEffect(() => {
    if (!executeRecaptcha) {
      return;
    }

    handleStandAloneIssueResponse(approved);
  }, [executeRecaptcha, approved]);

  const config = {
    [StandaloneIssueResponseType.Success]: {
      title: "Thank you!",
      description: message,
      hasImage: true,
      showOrderButton: true,
    },
    [StandaloneIssueResponseType.NeedsConfirmation]: {
      title: "Please confirm your selection below.",
      description: message,
      hasImage: false,
      showOrderButton: false,
      children: (
        <div className="tw-mt-[15px] tw-flex tw-flex-row tw-justify-center">
          <StandardButton
            style="primary-blue"
            onClick={() => {
              handleStandAloneIssueResponse(true);
            }}
            className="tw-mr-[20px]"
          >
            Approve
          </StandardButton>
          <StandardButton
            style="warning"
            onClick={() => {
              handleStandAloneIssueResponse(false);
            }}
          >
            Reject
          </StandardButton>
        </div>
      ),
    },
    [StandaloneIssueResponseType.InvalidToken]: {
      title: message,
      description: "Please login to versemedical.com and view the order.",
      hasImage: false,
      showOrderButton: true,
    },
    [StandaloneIssueResponseType.Retry]: {
      accentTitle: "Just one more step...",
      title:
        "To confirm you're human, please re-approve or reject the message.",
      description:
        "We've detected that the action may have been triggered by a bot. For security reasons, we require a human to verify the approval or rejection of the order note.",
      hasImage: false,
      showOrderButton: false,
      children: (
        <>
          <div className="tw-flex tw-flex-row tw-gap-x-[16px]">
            <button
              onClick={() => {
                handleStandAloneIssueResponse(true);
              }}
              className="tw-flex tw-flex-col tw-items-center tw-rounded-[8px] tw-border-[1px] tw-border-solid tw-border-gray-light-1 tw-bg-white tw-px-[72px] tw-py-[18px] hover:tw-bg-gray-light-4"
            >
              <FontAwesomeIcon
                icon={faCheck}
                className="tw-h-[43px] tw-w-[28px] tw-text-green-1"
              />
              Approve
            </button>
            <button
              onClick={() => {
                handleStandAloneIssueResponse(false);
              }}
              className="tw-flex tw-flex-col tw-items-center tw-rounded-[8px] tw-border-[1px] tw-border-solid tw-border-gray-light-1 tw-bg-white tw-px-[72px] tw-py-[18px] hover:tw-bg-gray-light-4"
            >
              <FontAwesomeIcon
                icon={faXmark}
                className="tw-h-[43px] tw-w-[28px] tw-text-red-1"
              />
              Reject
            </button>
          </div>
          <a
            href={`https://versemedical.com/provider_orders/view_order/${orderNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-mx-auto tw-flex tw-w-fit tw-cursor-pointer tw-flex-row tw-items-center tw-justify-center tw-gap-x-[8px] !tw-text-gray-3 hover:tw-underline"
          >
            <P2 className="tw-mb-0 tw-text-center tw-text-gray-3">
              View the order
            </P2>
            <FontAwesomeIcon
              icon={faArrowUpRightFromSquare}
              className="tw-text-gray-3"
            />
          </a>
        </>
      ),
      isRetry: true,
    },
  };
  const responseConfig = response ? config[response] : undefined;

  return (
    <div className="tw-mx-auto tw-my-[40px] tw-flex tw-w-[500px] tw-flex-col tw-items-center tw-gap-y-[32px] tw-rounded-md tw-p-[32px] tw-shadow-sm">
      {loading ? (
        <Loading />
      ) : (
        responseConfig && (
          <TextContent {...responseConfig} orderNumber={orderNumber} />
        )
      )}
    </div>
  );
};

const StandAloneIssueResponse = () => {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={"6Le5QlsrAAAAAPjoWmxGMrRMg6EtVgfkOGFTjSiF"}
    >
      <StandAloneIssueResponseContent />
    </GoogleReCaptchaProvider>
  );
};

export default StandAloneIssueResponse;
